/**
 * Programmatic SEO — Page « Factur-X pour [secteur] »
 * Target keyword: "factur-x [métier]" / "facturation électronique [secteur]"
 */
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, FileText, Sparkles, Shield, Zap } from 'lucide-react';
import secteurs from '../../data/seo/secteurs.json';

export default function SecteurPage() {
  const { slug } = useParams();
  const sect = secteurs.find((s) => s.slug === slug);

  if (!sect) {
    return <Navigate to="/" replace />;
  }

  const others = secteurs.filter((s) => s.slug !== sect.slug).slice(0, 6);

  const title = `Factur-X pour ${sect.name} | Logiciel conforme 2026 | SmartFacture AI`;
  const description = `Logiciel de facturation Factur-X spécialement adapté aux ${sect.name}. Conformité réforme 2026, scanner IA, Copilote conversationnel. Essai gratuit.`;
  const url = `https://smartfacture-ai.com/factur-x/${sect.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://smartfacture-ai.com/' },
      { '@type': 'ListItem', position: 2, name: 'Factur-X par métier', item: 'https://smartfacture-ai.com/factur-x' },
      { '@type': 'ListItem', position: 3, name: sect.title, item: url },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `Les ${sect.name} doivent-ils émettre des factures électroniques en 2026 ?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Oui. Tous les ${sect.name} assujettis à la TVA en France sont concernés par la réforme de la facturation électronique. Émission obligatoire selon le calendrier progressif : grandes entreprises au 1er septembre 2026, TPE/PME au 1er septembre 2027.`,
        },
      },
      {
        '@type': 'Question',
        name: `SmartFacture AI est-il adapté aux ${sect.name} ?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Oui, SmartFacture AI prend en charge les spécificités fiscales des ${sect.name} : ${sect.specifics.join(', ')}. Format Factur-X natif, conformité 2026 garantie.`,
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAFFF7]" data-testid="seo-secteur-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <nav className="max-w-5xl mx-auto px-6 pt-8 pb-2 text-xs text-slate-500" aria-label="Fil d'Ariane">
        <Link to="/" className="hover:underline">Accueil</Link>
        <span className="mx-1.5">›</span>
        <span className="text-slate-700">Factur-X par métier</span>
        <span className="mx-1.5">›</span>
        <span className="text-slate-900 font-medium">{sect.title}</span>
      </nav>

      <section className="max-w-5xl mx-auto px-6 pt-8 pb-16">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold mb-5">
          <FileText className="h-3.5 w-3.5" />
          Conformité Factur-X 2026
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-5">
          Factur-X pour <span className="text-emerald-600">{sect.name}</span>
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mb-7">
          {sect.blurb} SmartFacture AI génère des factures conformes Factur-X (PDF/A-3 + XML), spécifiquement pensées pour les contraintes fiscales des {sect.name} en France.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/register" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5" data-testid="seo-secteur-cta-register">
            Essayer gratuitement <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/pricing" className="inline-flex items-center gap-2 bg-white text-slate-700 font-semibold px-6 py-3 rounded-xl border border-slate-200 hover:border-emerald-300 transition">
            Voir les tarifs
          </Link>
        </div>
      </section>

      {/* Specifics */}
      <section className="bg-white py-16 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Spécificités fiscales des {sect.name}
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Chaque métier a ses obligations. Voici ce que SmartFacture AI gère automatiquement pour vous :
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {sect.specifics.map((spec) => (
              <div key={spec} className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-5 flex gap-3">
                <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{spec}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why SmartFacture */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-slate-900 mb-3">
          Pourquoi les {sect.name} choisissent SmartFacture AI
        </h2>
        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          {[
            { icon: Sparkles, title: 'Copilote IA en français', desc: 'Créez vos factures en une phrase. Aucun autre logiciel français ne propose cette fonctionnalité.' },
            { icon: Shield, title: 'Factur-X natif', desc: 'PDF/A-3 + XML conforme à la norme EN16931. Prêt pour le PPF et toutes les PDP agréées DGFiP.' },
            { icon: Zap, title: 'Scanner IA OCR', desc: "Photographiez vos reçus, l'IA extrait montant, TVA, date. Gain de temps massif au quotidien." },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-emerald-100 rounded-2xl p-6">
              <div className="h-11 w-11 rounded-xl bg-emerald-500 flex items-center justify-center mb-4 shadow-md shadow-emerald-500/20">
                <f.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 border-y border-emerald-100">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Questions fréquentes des {sect.name}</h2>
          <div className="space-y-5">
            <details className="group bg-emerald-50/30 border border-emerald-100 rounded-xl p-5">
              <summary className="font-semibold text-slate-900 cursor-pointer">Les {sect.name} doivent-ils émettre des factures électroniques en 2026 ?</summary>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                Oui. Tous les {sect.name} assujettis à la TVA en France sont concernés. Émission obligatoire selon le calendrier progressif : grandes entreprises au 1er septembre 2026, TPE/PME (la majorité) au 1er septembre 2027.
              </p>
            </details>
            <details className="group bg-emerald-50/30 border border-emerald-100 rounded-xl p-5">
              <summary className="font-semibold text-slate-900 cursor-pointer">SmartFacture AI gère-t-il les spécificités des {sect.name} ?</summary>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                Oui : {sect.specifics.join(', ').toLowerCase()}. Tout est paramétrable depuis votre profil entreprise.
              </p>
            </details>
            <details className="group bg-emerald-50/30 border border-emerald-100 rounded-xl p-5">
              <summary className="font-semibold text-slate-900 cursor-pointer">Combien coûte SmartFacture AI pour un {sect.name.replace('s ', ' ').replace(/s$/, '')} ?</summary>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                Gratuit jusqu'à 5 factures/mois (plan Découverte, sans CB). Au-delà, le plan Starter à 14€/mois (20 factures/mois) suffit à la plupart des indépendants.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Other sectors */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Factur-X pour d'autres métiers</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {others.map((s) => (
            <Link
              key={s.slug}
              to={`/factur-x/${s.slug}`}
              className="bg-white border border-slate-200 hover:border-emerald-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:text-emerald-700 transition flex items-center justify-between group"
              data-testid="seo-other-sector-link"
            >
              <span>Factur-X {s.name}</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-emerald-500 transition" />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Prêt à être conforme 2026 ?
          </h2>
          <p className="text-slate-700 mb-7">
            SmartFacture AI s'adapte automatiquement aux contraintes des {sect.name}. Essai gratuit, sans CB.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-7 py-3.5 rounded-xl shadow-xl shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
            data-testid="seo-secteur-cta-bottom"
          >
            Commencer gratuitement <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
